import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import {
  Form,
  Input,
  Button,
  Row,
  Col,
  Spin,
  Card,
  Typography,
  Tabs,
  message,
} from 'antd';
import {
  CalculatorOutlined,
  DiffOutlined,
  ImportOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import FormSubmitControls from 'components/FormSubmitControls';
import Table from 'components/Table';
import Select from 'components/Select';
import Editor from 'components/Editor/index';
import ModalDelete from 'components/ModalDelete';
import NumericInput from 'components/NumericInput';
import PlainTransfer from 'components/PlainTransfer';
import toCurrency from 'utils/toCurrency';
import { createDecimalRegExp } from 'utils/patterns';
import { onError } from 'utils/handlers';
import {
  baseURI,
  deleteTipoDeGasto,
  getTipoDeGasto,
  getTiposDeGastos,
  patchTipoDeGasto,
  postTipoDeGasto,
} from 'api/recaudacion/tiposDeGasto';
import { getConceptoDeIngresos, getConceptosDeIngresos } from 'api/catalogos/conceptosDeIngresos';
import { getCatalogoDePadrones } from 'api/catalogos';
import { getTiposDeAplicacionParaCargos, getPeriodos } from 'api/recaudacion/index';
import {
  getMonedas,
  getSalariosMinimos,
  getUMAs,
} from 'api/configuracion';
import { getTiposDeDescuentos } from 'api/recaudacion/tiposDeDescuento';
import { getVariables } from 'api/configuracion/variables';
import { hasActionPermission, ActionsNames } from 'utils/permission/index';
import { aplicacionesCargosEnum } from 'views/Tramites/GPM/enums';

const { Text } = Typography;
const { TabPane } = Tabs;

const catalogsModels = [
  'catalogodetarifa',
  'conceptodeingreso',
  'moneda',
  'padron',
  'periodo',
  'tipodeaplicacionparacargo',
  'tipodedato',
  'tipodedescuento',
  'variable',
];

const tiposDeAplicacionDisponibles = [
  aplicacionesCargosEnum.CAPTURABLE,
  aplicacionesCargosEnum.FIJO,
  aplicacionesCargosEnum.FORMULA,
  aplicacionesCargosEnum.SQL_FUNCTION,
];

export const permissionConfig = {
  mainPermission: 'view_tipodegasto',
  required: [
    'add_tipodegasto',
    'change_tipodegasto',
  ],
  catalogsModels,
  customActions: [],
};

// eslint-disable-next-line react/prop-types
const TipoDeGasto = ({ permission }) => {
  const periodoFiscalSelected = useSelector(({ auth }) => auth.periodoFiscalSelected);
  const monedaDefault = useSelector(({ auth }) => auth.entidad?.moneda);
  const {
    estadosGlobales,
    padrones,
    tiposDeAplicacionCargos: _tiposDeAplicacion,
  } = useSelector((state) => state.catalogs);
  const tiposDeAplicacion = _tiposDeAplicacion
    .filter((e) => tiposDeAplicacionDisponibles.includes(e.id));

  const [decimal] = createDecimalRegExp(monedaDefault);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [visible, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [data, setData] = useState([]);
  const [monedas, setMonedas] = useState([]);
  const [tiposDescuentos, setTiposDescuentos] = useState([]);
  const [salario, setSalario] = useState([]);
  const [uma, setUma] = useState([]);
  const [totalSalario, setTotalSalario] = useState([]);
  const [totalUma, setTotalUma] = useState([]);
  const [monedaSelected, setMonedaSelected] = useState(monedaDefault?.id);
  const [activeTab, setActiveTab] = useState('general');
  const [periodos, setPeriodos] = useState([]);
  const [variables, setVariables] = useState([]);
  const [hasAllViewPermission, setHasAllViewPermission] = useState(true);
  const [conceptosDeIngresoTemp, setConceptosDeIngresoTemp] = useState({ results: [] });

  const conceptosDeIngreso = useRef({ results: [] });

  const [formGeneral] = Form.useForm();
  const [formCalculo] = Form.useForm();
  const [formAccesorios] = Form.useForm();
  const [formVariables] = Form.useForm();

  const padronSelected = Form.useWatch('padron', formGeneral);
  const tipoDeAplicacionSelected = Form.useWatch('tipo_de_aplicacion', formCalculo);

  const fetchData = async () => {
    setLoading(true);
    const _data = await getTiposDeGastos({ periodo_fiscal: periodoFiscalSelected?.id });
    setData(_data);
    setLoading(false);
  };

  const onCancel = () => {
    setVisible(false);
    formAccesorios.resetFields();
    formVariables.resetFields();
    formCalculo.resetFields();
    formGeneral.resetFields();
    setSelectedRowKeys([]);
    setTotalSalario([]);
    setTotalUma([]);
    setMonedaSelected(monedaDefault?.id);
    setActiveTab('general');
    setConceptosDeIngresoTemp({ results: [] });
  };

  useEffect(() => {
    let mounted = true;
    const validatePermission = () => {
      const _hasAllViewPermission = catalogsModels
        .map((m) => hasActionPermission(m, ActionsNames.view))
        .every((e) => e);
      if (!_hasAllViewPermission) {
        message.warn('Hacen falta permisos en los catálogos relacionados a este módulo');
      }
      if (mounted) {
        setHasAllViewPermission(_hasAllViewPermission);
      }
    };
    const fetchAll = async () => {
      const [
        dataMonedas,
        dataPeriodos,
        dataVariables,
        dataTiposDeDescuentos,
      ] = await Promise.all([
        getMonedas(),
        getPeriodos(),
        getVariables({ es_script: true }),
        getTiposDeDescuentos({ aplicacion_del_descuento: 4 }),
        getCatalogoDePadrones(),
        getTiposDeAplicacionParaCargos(),
      ]);

      if (mounted) {
        setPeriodos(dataPeriodos);
        setVariables(dataVariables);
        setTiposDescuentos(dataTiposDeDescuentos);
        setMonedas(dataMonedas.filter((e) => e.id === monedaDefault.id || e.calculable));
        setLoading(false);
      }

      await fetchData();
    };

    validatePermission();
    fetchAll();

    return () => { mounted = false; };
    // eslint-disable-next-line
  }, [periodoFiscalSelected]);

  const onFinish = async () => {
    try {
      setLoading(true);
      await formGeneral.validateFields();
      await formCalculo.validateFields();
      await formVariables.validateFields();
      const values = {
        ...formCalculo.getFieldsValue(),
        ...formAccesorios.getFieldsValue(),
        ...formGeneral.getFieldsValue(),
        ...formVariables.getFieldsValue(),
        periodo_fiscal: periodoFiscalSelected?.id,
      };
      const [key] = selectedRowKeys;
      const saved = await (key ? patchTipoDeGasto(key, values) : postTipoDeGasto(values));
      if (saved) {
        onCancel();
        await fetchData();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading, [formGeneral, formCalculo, formVariables], setActiveTab);
    }
  };

  const deleteItem = async () => {
    setLoading(true);
    if (selectedRowKeys.length) {
      const [key] = selectedRowKeys;
      const deleted = await deleteTipoDeGasto(key);
      if (deleted) {
        setVisibleAlert(false);
        onCancel();
        await fetchData();
      }
    }
    setLoading(false);
  };

  const calcularImporte = (val) => {
    const selected = formGeneral.getFieldValue('moneda');
    if (selected === 3) {
      const totalValSalario = salario.salario_minimo_nominal * val;
      const formatedValue = toCurrency(totalValSalario || 0);
      setTotalSalario(formatedValue);
    }
    if (selected === 4) {
      const totalValUma = uma.valor_diario * val;
      const formatedValue = toCurrency(totalValUma || 0);
      setTotalUma(formatedValue);
    }
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    formGeneral.setFieldsValue(record);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const onClickAdd = () => {
    onCancel();
    setVisible(true);
  };

  const filterConceptosDeIngresos = async () => {
    formGeneral.resetFields(['concepto_de_ingreso', 'clave', 'descripcion']);
  };

  const onClickEdit = async () => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const record = await getTipoDeGasto(key);
    calcularImporte(record.importe);
    setSelectedRowKeys([record.id]);
    setMonedaSelected(record.moneda);
    const concepto = await getConceptoDeIngresos(record.concepto_de_ingreso);
    setConceptosDeIngresoTemp({ count: 1, results: [concepto] });
    formGeneral.setFieldsValue(record);
    formCalculo.setFieldsValue(record);
    formAccesorios.setFieldsValue(record);
    formVariables.setFieldsValue(record);
    setVisible(true);
    setLoading(false);
  };

  const onClickDelete = () => {
    setVisibleAlert(true);
  };

  const requiredRule = { required: true };

  const rules = {
    required: [requiredRule],
    importe: [
      {
        validator: async (_, val) => {
          if (!val) {
            throw new Error('El campo es requerido');
          } else if (!decimal.test(val)) {
            throw new Error('Ingrese un valor decimal válido');
          }
        },
      },
    ],
    importe_maximo: [
      requiredRule,
      {
        validator: async () => {
          const values = formCalculo.getFieldsValue(['importe_minimo', 'importe_maximo']);
          const importe_minimo = parseFloat(values.importe_minimo);
          const importe_maximo = parseFloat(values.importe_maximo);
          if (values.importe_maximo) {
            if (!decimal.test(values.importe_maximo)) {
              throw new Error('Ingrese un valor decimal válido');
            } else if (importe_maximo <= importe_minimo) {
              throw new Error('El campo debe ser mayor al Importe Mínimo');
            }
          }
        },
      },
    ],
    importe_minimo: [
      requiredRule,
      {
        validator: async () => {
          const values = formCalculo.getFieldsValue(['importe_minimo', 'importe_maximo']);
          const importe_minimo = parseFloat(values.importe_minimo);
          const importe_maximo = parseFloat(values.importe_maximo);
          if (values.importe_minimo) {
            if (!decimal.test(values.importe_minimo)) {
              throw new Error('Ingrese un valor decimal válido');
            } else if (importe_minimo >= importe_maximo) {
              throw new Error('El campo debe ser menor al Importe Máximo');
            }
          }
        },
      },
    ],
  };

  const columns = [
    {
      titleText: 'Clave',
      dataIndex: 'clave',
      key: 'clave',
      width: 250,
      render: (text) => (
        <Typography.Text
          className="no-bold"
          ellipsis={{ tooltip: text }}
        >
          {text}
        </Typography.Text>
      ),
    },
    {
      titleText: 'Descripción',
      dataIndex: 'descripcion',
      key: 'descripcion',
      width: 500,
      render: (text) => (
        <Typography.Text
          className="no-bold"
          ellipsis={{ tooltip: text }}
        >
          {text}
        </Typography.Text>
      ),
    },
    {
      titleText: 'Tipo de Aplicación',
      dataIndex: 'tipo_de_aplicacion',
      key: 'tipo_de_aplicacion',
      width: 200,
      render: (val) => (tiposDeAplicacion.find((item) => item.id === val)?.descripcion),
    },
    {
      titleText: 'Padrón',
      dataIndex: 'padron',
      key: 'padron',
      width: 200,
      render: (val) => {
        const padron = padrones.find((item) => item.id === val);
        return (
          <Typography.Text
            className="no-bold"
            ellipsis={{ tooltip: padron?.descripcion }}
          >
            {padron?.descripcion}
          </Typography.Text>
        );
      },
    },
    {
      titleText: 'Observaciones',
      dataIndex: 'observaciones',
      key: 'observaciones',
      width: 700,
      render: (text) => (
        <Typography.Text
          className="no-bold"
          ellipsis={{ tooltip: text }}
        >
          {text || 'N/A'}
        </Typography.Text>
      ),
    },
  ];

  const onSelect = async (moneda) => {
    try {
      formGeneral.setFieldsValue({
        importe: undefined,
      });
      setMonedaSelected(moneda);
      setLoading(true);
      if (moneda === 3) {
        if (!salario.length) {
          const resSalarios = await getSalariosMinimos();
          setSalario(resSalarios.pop());
          setUma([]);
        }
      }
      if (moneda === 4) {
        if (!uma.length) {
          const resUMA = await getUMAs();
          setUma(resUMA.pop());
          setSalario([]);
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const createClave = async (id, clave, filtered) => {
    const next = parseInt(filtered[filtered.length - 1]?.clave.split('-').pop() || 0, 10) + 1;
    formGeneral.setFieldsValue({
      clave: `${clave}-${next}`,
    });
  };

  const onSelectConcepto = async (selected) => {
    if (selected) {
      const { id, clave, descripcion } = conceptosDeIngreso.current.results
        .find((item) => item.id === selected);
      const claveDescripcion = descripcion.split(' ').map((e) => e[0]).join('');
      formGeneral.resetFields(['descripcion', 'clave']);
      const filtered = data.filter((e) => e.concepto_de_ingreso === id);
      await createClave(id, `${clave}-${claveDescripcion.toLowerCase()}`, filtered);
      formGeneral.setFieldsValue({
        descripcion,
      });
    } else {
      formGeneral.resetFields(['descripcion', 'clave']);
    }
  };

  const onSelectTipoDeAplicacion = (selected) => {
    if (selected !== aplicacionesCargosEnum.FORMULA) {
      formGeneral.setFieldsValue({
        tipo_de_dato: undefined,
        parametro_descripcion: undefined,
        catalogo_de_tarifa: undefined,
      });
    }
  };

  return (
    <Row align="center" justify="center" className="container">
      <Spin tip="Cargando..." spinning={loading}>
        {!visible ? (
          <Table
            cols={columns}
            data={data}
            rowSelection={rowSelection}
            handleOnRowClick={handleOnRowClick}
            controls={{
              onClickAdd,
              onClickEdit,
              onClickDelete,
            }}
            rowKey="id"
            baseURI={baseURI}
            allowImport
            permission={permission}
            hasAllViewPermission={hasAllViewPermission}
          />
        ) : (
          <Card
            className="form-container"
            title={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Tipo de Gasto`}
            extra={(
              <FormSubmitControls
                onFinish={onFinish}
                onCancel={onCancel}
                loading={loading}
                permission={permission}
              />
            )}
            bordered={false}
          >
            <Tabs
              defaultActiveKey="general"
              type="card"
              onChange={setActiveTab}
              activeKey={activeTab}
            >
              <TabPane
                tab={(
                  <span>
                    <ImportOutlined />
                    General
                  </span>
                )}
                key="general"
                forceRender
              >
                <Form
                  layout="vertical"
                  form={formGeneral}
                  onFinish={() => onFinish()}
                  name="general"
                  initialValues={{ estados_globales: 1 }}
                >
                  <Row gutter={10}>
                    <Col xs={24} sm={24} md={8}>
                      <Form.Item
                        rules={rules.required}
                        name="padron"
                        label="Padrón"
                        hasFeedback
                      >
                        <Select
                          onChange={(p) => {
                            filterConceptosDeIngresos();
                            formCalculo.setFieldsValue({
                              padron: p,
                            });
                          }}
                          dataSource={padrones}
                          disabled={!!selectedRowKeys.length}
                          onClear={() => {
                            formGeneral.resetFields([
                              'concepto_de_ingreso',
                              'clave',
                              'descripcion',
                            ]);
                          }}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={24} md={8}>
                      <Form.Item
                        rules={rules.required}
                        name="concepto_de_ingreso"
                        label="Concepto de ingreso"
                        hasFeedback
                      >
                        <Select
                          dataSource={conceptosDeIngresoTemp.results.length
                            ? conceptosDeIngresoTemp : conceptosDeIngreso.current}
                          usingExternalDataSource={Boolean(conceptosDeIngresoTemp.results.length)}
                          onChange={onSelectConcepto}
                          disabled={!!selectedRowKeys.length
                            || !formGeneral.getFieldValue('padron')}
                          pagination={{
                            getter: getConceptosDeIngresos,
                            setter: (dataSource) => {
                              conceptosDeIngreso.current = dataSource;
                            },
                            config: { qName: 'descripcion' },
                            params: { padron: padronSelected },
                          }}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={24} md={8}>
                      <Form.Item
                        rules={rules.required}
                        name="clave"
                        label="Clave"
                        hasFeedback
                      >
                        <Input allowClear disabled={!!selectedRowKeys.length} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={24} md={16}>
                      <Form.Item
                        rules={rules.required}
                        name="descripcion"
                        label="Descripción"
                        hasFeedback
                      >
                        <Input.TextArea allowClear autoSize={{ minRows: 3, maxRows: 5 }} />
                      </Form.Item>
                    </Col>

                    <Col xs={24} sm={24} md={16}>
                      <Form.Item
                        name="observaciones"
                        label="Observaciones"
                      >
                        <Input allowClear maxLength={200} />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={10}>
                    <Col xs={24} sm={24} md={8}>
                      <Form.Item
                        name="es_multa"
                        label="¿Es Multa?"
                        hasFeedback
                      >
                        <Select trueFalse />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={24} md={8}>
                      <Form.Item
                        name="periodo"
                        label="Periodo"
                        hasFeedback
                      >
                        <Select dataSource={periodos} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={24} md={8}>
                      <Form.Item
                        name="estados_globales"
                        label="Estado"
                        rules={rules.required}
                        hasFeedback
                      >
                        <Select
                          disabled={!selectedRowKeys.length}
                          dataSource={estadosGlobales}
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Form.Item hidden>
                    <Button htmlType="submit" />
                  </Form.Item>
                </Form>
              </TabPane>
              <TabPane
                tab={(
                  <span>
                    <CalculatorOutlined />
                    Parámetros de Cálculo
                  </span>
                )}
                key="calculo"
                forceRender
              >
                <Form
                  layout="vertical"
                  form={formCalculo}
                  onFinish={() => onFinish()}
                  name="calculo"
                  initialValues={{ moneda: monedaDefault?.id }}
                >
                  <Row gutter={10}>
                    <Col xs={24} sm={24} md={8}>
                      <Form.Item
                        rules={rules.required}
                        name="moneda"
                        label="Moneda"
                        hasFeedback
                      >
                        <Select
                          onChange={(val) => {
                            if (val === 2) {
                              setSalario([]);
                            }
                            if (val === 3) {
                              setUma([]);
                            }
                            onSelect(val);
                          }}
                          dataSource={monedas}
                          keyLabelRender
                        />
                      </Form.Item>
                    </Col>

                    <Col xs={24} sm={24} md={8}>
                      <Form.Item
                        name="padron"
                        label="Padrón"
                        hasFeedback
                      >
                        <Select disabled dataSource={padrones} />
                      </Form.Item>
                    </Col>

                    <Col xs={24} sm={24} md={8}>
                      <Form.Item
                        rules={rules.required}
                        name="tipo_de_aplicacion"
                        label="Tipo de aplicación"
                        hasFeedback
                      >
                        <Select
                          onChange={onSelectTipoDeAplicacion}
                          dataSource={tiposDeAplicacion}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={24}>
                      {tipoDeAplicacionSelected === aplicacionesCargosEnum.CAPTURABLE && (
                        <Row gutter={10}>
                          <Col xs={24} sm={24} md={8}>
                            <NumericInput
                              rules={rules.importe_minimo}
                              name="importe_minimo"
                              label="Importe mínimo"
                              decimal
                            />
                          </Col>
                          <Col xs={24} sm={24} md={8}>
                            <NumericInput
                              rules={rules.importe_maximo}
                              name="importe_maximo"
                              label="Importe máximo"
                              decimal
                            />
                          </Col>
                        </Row>
                      )}
                      {tipoDeAplicacionSelected === aplicacionesCargosEnum.FIJO && (
                        <Row gutter={10}>
                          {monedaSelected === monedaDefault?.id && (
                            <Col
                              xs={24}
                              sm={24}
                              md={8}
                            >
                              <NumericInput
                                required
                                name="importe"
                                label="Importe"
                                onChange={calcularImporte}
                                rules={rules.importe}
                                decimal
                              />
                            </Col>
                          )}
                          {monedaSelected === 3 && (
                            <>
                              <Col
                                xs={24}
                                sm={24}
                                md={8}
                              >
                                <NumericInput
                                  required
                                  name="importe"
                                  label="Cantidad"
                                  onChange={calcularImporte}
                                  decimal
                                />
                              </Col>
                              <Col
                                xs={24}
                                sm={24}
                                md={8}
                              >
                                <Form.Item
                                  label=" "
                                >
                                  <Text>
                                    {' '}
                                    {`Valor Estimado: ${totalSalario}`}
                                  </Text>
                                </Form.Item>
                              </Col>
                            </>
                          )}
                          {monedaSelected === 4 && (
                            <>
                              <Col
                                xs={24}
                                sm={24}
                                md={8}
                              >
                                <NumericInput
                                  required
                                  name="importe"
                                  label="Cantidad"
                                  onChange={calcularImporte}
                                  decimal
                                />
                              </Col>
                              <Col
                                xs={24}
                                sm={24}
                                md={8}
                              >
                                <Form.Item
                                  label=" "
                                >
                                  <Text>
                                    {' '}
                                    {`Valor Estimado: ${totalUma}`}
                                  </Text>
                                </Form.Item>
                              </Col>
                            </>
                          )}
                        </Row>
                      )}
                    </Col>
                    {(tipoDeAplicacionSelected === aplicacionesCargosEnum.FORMULA
                      || tipoDeAplicacionSelected === aplicacionesCargosEnum.SQL_FUNCTION) && (
                      <Editor form={formCalculo} rules={rules.required} cargo />
                    )}
                  </Row>
                  <Form.Item hidden>
                    <Button htmlType="submit" />
                  </Form.Item>
                </Form>
              </TabPane>
              <TabPane
                tab={(
                  <span>
                    <DiffOutlined />
                    Accesorios
                  </span>
                )}
                key="accesorios"
              >
                <Form
                  layout="vertical"
                  form={formAccesorios}
                  onFinish={() => onFinish()}
                  name="accesorios"
                >
                  <Col span={24}>
                    <PlainTransfer
                      dataSource={tiposDescuentos}
                      label="Tipo de descuento"
                      formItemName="tipos_de_descuento"
                      form={formAccesorios}
                      filterProp="descripcion"
                    />
                  </Col>
                  <Form.Item hidden>
                    <Button htmlType="submit" />
                  </Form.Item>
                </Form>
              </TabPane>
              <TabPane
                tab={(
                  <span>
                    <SettingOutlined />
                    Variables
                  </span>
                )}
                key="variables"
                forceRender
                disabled={tipoDeAplicacionSelected !== aplicacionesCargosEnum.FORMULA}
              >
                <Form
                  form={formVariables}
                  layout="vertical"
                  onFinish={() => onFinish()}
                  name="variables"
                >
                  <Row>
                    <Col span={24}>
                      <PlainTransfer
                        dataSource={variables}
                        label="Variables"
                        formItemName="variables"
                        form={formVariables}
                        filterProp="descripcion_de_variable"
                      />
                    </Col>
                  </Row>
                </Form>
              </TabPane>
            </Tabs>
          </Card>
        )}
        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => {
            setVisibleAlert(false);
            setSelectedRowKeys([]);
          }}
          visible={visibleAlert}
          content={`Tipo de Gasto ${formGeneral.getFieldValue('descripcion')}`}
          loading={loading}
        />
      </Spin>
    </Row>
  );
};

export default TipoDeGasto;
